import { IJoinChoice, IPathChoiceRequired, isJoinHop, JoinPath, JoinRelationEntry } from './interfaces/query.interface';

/**
 * `POST build-run` answered PATH_CHOICE_REQUIRED (GOP-91): nothing ran, the user has to pick one
 * path per choice. `buildAndRun()` raises it as an error so no caller mistakes the answer for an
 * empty result.
 */
export class PathChoiceRequiredError extends Error {
  constructor(readonly response: IPathChoiceRequired) {
    super(response.message);
    this.name = 'PathChoiceRequiredError';
  }

  get choices(): IJoinChoice[] {
    return this.response.choices;
  }
}

/** Whether a build-run response is the PATH_CHOICE_REQUIRED answer rather than a result. */
export function isPathChoiceRequired(response: unknown): response is IPathChoiceRequired {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return false;
  const body = response as Partial<IPathChoiceRequired>;
  return body.status === 'PATH_CHOICE_REQUIRED' && Array.isArray(body.choices);
}

/** Identity of a choice, or of the path entry that settles it: the table and its instance name. */
export function choiceKey(choice: { table: string; as?: string } | JoinPath): string {
  const table = 'table' in choice ? choice.table : choice.to;
  return choice.as === undefined ? table : `${table} as ${choice.as}`;
}

/**
 * `config` with the picked path entries in `joinRelations`, each added as it is. An earlier path
 * entry for the same table and instance is replaced; hops and other entries stay, in their order.
 */
export function withChosenPaths<T extends { joinRelations?: JoinRelationEntry[] }>(config: T, picks: JoinPath[]): T {
  const picked = new Set(picks.map(choiceKey));
  const kept = (config.joinRelations ?? []).filter((entry) => isJoinHop(entry) || !picked.has(choiceKey(entry)));
  return { ...config, joinRelations: [...kept, ...picks] };
}

/** How a path reads in a list: a title, and the lines under it. */
export interface IPathTitle {
  title: string;
  details: string[];
}

/**
 * What tells each path of a choice apart (WB-259). Paths through different tables are titled with
 * their route, their join columns under it; paths through the same tables (several relations
 * between two tables) would all read the same route, so their columns become the title and the
 * route goes under it.
 */
export function pathTitles(paths: { route: string; joinedOn: string[] }[]): IPathTitle[] {
  const routes = new Map<string, number>();
  paths.forEach((path) => routes.set(path.route, (routes.get(path.route) ?? 0) + 1));
  return paths.map((path) =>
    (routes.get(path.route) ?? 0) > 1 && path.joinedOn.length
      ? { title: path.joinedOn.join(' · '), details: [path.route] }
      : { title: path.route, details: path.joinedOn }
  );
}
